import { HotspotMarkersView } from '@guidde/design-system'
import { useEffect, useMemo, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'

import { type PlaybookType, type QuickGuiddeType } from 'app/types'
import { isQG } from 'modules'

import { toHotspotMarker } from './hotspot-adapter'
import { useHotspotPlayback } from './use-hotspot-playback'
import { useVideoContentRect, useVideoElement } from './use-video-content-rect'

type Props = {
    containerRef: RefObject<HTMLElement>
    playbook: PlaybookType | QuickGuiddeType
}

/**
 * Embed hotspot overlay: reads the playbook, drives playback/gating and geometry via local hooks,
 * maps hotspot layers to presentational props, and renders the DS marker view. Mirrors the webapp
 * viewer but trimmed for embed — no processing screen, no cover "Get started", just the interactive
 * hotspots on a finished video.
 */
export const HotspotsOverlay = ({ containerRef, playbook }: Props) => {
    const video = useVideoElement(containerRef)

    // In fullscreen the player promotes its own element to the top layer; portal the overlay into it.
    const [fullscreenEl, setFullscreenEl] = useState<HTMLElement | null>(null)
    useEffect(() => {
        const sync = () => {
            const el = document.fullscreenElement as HTMLElement | null
            setFullscreenEl(el && video && el.contains(video) ? el : null)
        }
        sync()
        document.addEventListener('fullscreenchange', sync)
        return () => document.removeEventListener('fullscreenchange', sync)
    }, [video])

    // Bumped when the viewer clicks the scrim (not a hotspot) → markers fire a "look here" burst.
    const [nudgeSignal, setNudgeSignal] = useState(0)

    const hasHotspots = useMemo(
        () =>
            isQG(playbook) && playbook.steps.some(step => (step.layers ?? []).some(layer => layer.type === 'hotspot')),
        [playbook],
    )

    const { hotspots, isGated, runAction } = useHotspotPlayback(playbook, video, hasHotspots)

    // Frame size is constant across steps (window dimensions don't change) — resolve once.
    const frameSize = useMemo(() => {
        if (!isQG(playbook) || !playbook.steps.length) return null
        const dims = playbook.steps[0].windowDimensions
        return dims ? { width: dims.innerWidth, height: dims.innerHeight } : null
    }, [playbook])

    const contentRect = useVideoContentRect(video, fullscreenEl ?? containerRef.current, frameSize)

    const markers = useMemo(() => hotspots.map(toHotspotMarker), [hotspots])

    if (!contentRect || !frameSize || !markers.length) return null

    const content = (
        <HotspotMarkersView
            markers={markers}
            contentRect={contentRect}
            frameScale={contentRect.width / frameSize.width}
            isGated={isGated}
            nudgeSignal={nudgeSignal}
            onScrimClick={() => setNudgeSignal(signal => signal + 1)}
            onMarkerClick={id => {
                const hotspot = hotspots.find(h => h.id === id)
                if (hotspot) runAction(hotspot)
            }}
        />
    )

    return fullscreenEl ? createPortal(content, fullscreenEl) : content
}
